import { EmailInput, Input, PasswordInput } from '@library/components/inputs';
import { PrimaryButton, SecondaryButton } from '@library/components/buttons';
import { isEqualArrays, isValidArrayWithValues } from '@bo-utils/common';
import { useContext, useMemo, useState } from 'react';
import { Modal } from '@library/components/modals/Modal';
import type { UserRoleOption } from '@library/types/user';
import { UserRoleSettings } from '@bo-components/userRoles/UserRoleSettings';
import { UserRolesContext } from '@bo-hooks/user/useAdminUsersContext';
import { isValidEmail } from '@library/utils';
import { mapUserRolesAttributes } from '@bo-utils/user';
import { useLeagues } from '@bo-hooks/useLeagues';
import { useRoles } from '@bo-hooks/roles/useRoles';

export const CreateUserModal = ({
  isOpen,
  onCancelModal
}: {
  isOpen: boolean;
  onCancelModal: () => void;
}) => {
  const { leagueOptions } = useLeagues();
  const { roleComboboxOptions: roleOptions, rolesWithPermissions } = useRoles({
    roles: undefined
  });
  const { createAdminUser } = useContext(UserRolesContext);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userRoles, setUserRoles] = useState<Array<UserRoleOption>>([]);
  const userRoleOptions: Array<UserRoleOption> = useMemo(() => [], []);

  const saveDisabled = useMemo(
    () =>
      !firstName ||
      !lastName ||
      !(email && isValidEmail(email)) ||
      !password ||
      userRoles?.length === 0 ||
      isEqualArrays(userRoleOptions, userRoles, 'role.value') ||
      !isValidArrayWithValues(userRoles, 'role'),
    [email, firstName, lastName, password, userRoleOptions, userRoles]
  );

  const resetFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
  };

  const handleCancelModal = () => {
    resetFields();
    onCancelModal();
  };

  const handleSubmit = async () => {
    if (userRoles?.length) {
      const userRoleAttributes = mapUserRolesAttributes(userRoles);

      await createAdminUser({
        admin_user: {
          first_name: firstName,
          last_name: lastName,
          email,
          password,
          user_roles_attributes: userRoleAttributes
        }
      });
      handleCancelModal();
    }
  };

  return (
    <Modal isOpen={isOpen} onCancelModal={handleCancelModal}>
      <div className="bg-soFresh-100 border-soFresh-140 relative mx-auto w-[45rem] flex-col rounded-md border">
        <div className="text-soClean-100 border-soFresh-140 flex h-10 items-center justify-between rounded-t border-b border-solid px-4 font-medium">
          <span>Create New User</span>
        </div>
        <div className="flex max-h-[70vh] flex-col gap-y-2 overflow-y-scroll p-4">
          <Input
            labelName="First Name"
            value={firstName}
            onValueChange={setFirstName}
          />
          <Input
            labelName="Last Name"
            value={lastName}
            onValueChange={setLastName}
          />
          <EmailInput
            labelName="Email Address"
            value={email}
            onValueChange={setEmail}
            id="email"
            autoComplete="off"
          />
          {/* TODO: Decide on password rules */}
          <PasswordInput
            labelName="Password"
            value={password}
            onValueChange={setPassword}
            id="password"
            autoComplete="new-password"
          />
          <p className="text-soClean-100 rounded-md text-xs uppercase leading-6">
            Roles
          </p>
          <UserRoleSettings
            className="!p-0"
            leagueOptions={leagueOptions}
            roleOptions={roleOptions}
            rolesWithPermissions={rolesWithPermissions}
            userRoleOptions={userRoleOptions}
            onEditUserRoles={setUserRoles}
          />
        </div>
        <div className="border-soFresh-140 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
          <SecondaryButton title="Cancel" handleOnClick={handleCancelModal}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            title="Save"
            handleOnClick={handleSubmit}
            disabled={saveDisabled}
          >
            Save
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};
